<template>
	<div class="expandable-text" ref="rootEl">
		<div ref="containerEl" :style="{ transitionDuration: `${duration / 1000}s` }">
			<DsText text-type="body-sm" class="long-text">
				<slot name="longText"></slot>
			</DsText>
			<DsText text-type="body-sm" class="short-text">
				<slot name="shortText"></slot>
			</DsText>
		</div>
		<span role="button" tabindex="0" class="toggler" :aria-expanded="isOpen" @click.prevent="toggle">{{
			isOpen ? readLessLabel : readMoreLabel
		}}</span>
	</div>
</template>

<script setup lang="ts">
import { DsText } from '@coloplast/design-system';
import { onMounted, ref } from 'vue';

interface Props {
	initOpen?: boolean;
	readMoreLabel?: string;
	readLessLabel?: string;
	duration: number;
}

const props = withDefaults(defineProps<Props>(), {
	initOpen: false,
	duration: 200,
});

const rootEl = ref<HTMLDivElement>();
const containerEl = ref<HTMLDivElement>();
const isOpen = ref(props.initOpen);

function getTextHeight(type: 'long' | 'short') {
	const el = containerEl.value?.querySelector(`.${type}-text`) as HTMLParagraphElement | null;
	return el?.offsetHeight;
}

function toggle() {
	isOpen.value = !isOpen.value;
	if (!containerEl.value) return;

	if (isOpen.value) {
		delayExpandedClass(0);
		getTextHeight('long');
		containerEl.value.style.maxHeight = `${getTextHeight('long')}px`;
	} else {
		delayExpandedClass(props.duration);
		getTextHeight('short');
		containerEl.value.style.maxHeight = `${getTextHeight('short')}px`;
	}
}

function delayExpandedClass(delay: number) {
	setTimeout(() => {
		rootEl.value?.classList.toggle('expanded');
	}, delay);
}

onMounted(() => {
	containerEl.value!.style.maxHeight = `${getTextHeight('short')}px`;
});
</script>

<style lang="scss">
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

div.expandable-text {
	> div {
		position: relative;
		overflow: hidden;
		transition: max-height 0.2s ease-out;
	}

	.short-text {
		visibility: visible;
		position: static;
	}
	.long-text {
		visibility: hidden;
		position: absolute;
	}

	&.expanded {
		.long-text {
			visibility: visible;
			position: static;
		}
		.short-text {
			visibility: hidden;
			position: absolute;
		}
	}

	p {
		white-space: normal;
	}

	.toggler {
		color: ds-color('fg', 'link');
		margin-top: ds-space(5);
		font-weight: 500;
		display: block;
		font-size: 16px;

		@include t-respond-to-max('medium') {
			font-size: 14px;
		}

		// Removes the outline only for click-events, NOT keyboard events
		&:focus:not(:focus-visible) {
			outline: none;
		}
	}
}
</style>
